import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { DescriptionList } from "../../components/ui/DescriptionList";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { PageHeader } from "../../components/ui/PageHeader";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { type DeviceListRow } from "../../lib/api/devices";
import { type Role, usersApi } from "../../lib/api/users";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const ROLES: Role[] = ["superadmin", "platform_admin", "member"];
const ADMIN_ROLES: Role[] = ["superadmin", "platform_admin"];

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function UserEditorPage() {
  useLocale();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const isNew = !id || id === "new";

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [employeeCode, setEmployeeCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const detail = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersApi.get(id!),
    enabled: !isNew,
  });

  const devices = useQuery({
    queryKey: ["user", id, "devices"],
    queryFn: () => usersApi.listDevices(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (detail.data) {
      setEmail(detail.data.email);
      setFullName(detail.data.full_name);
      setRole(detail.data.role);
      setEmployeeCode(detail.data.employee_code);
      setIsActive(detail.data.is_active);
    }
  }, [detail.data]);

  const create = useMutation({
    mutationFn: () =>
      usersApi.create({
        email,
        full_name: fullName,
        role,
        employee_code: employeeCode,
        is_active: isActive,
        password: ADMIN_ROLES.includes(role) ? password : undefined,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      navigate(`/users/${created.id}`);
    },
  });

  const patch = useMutation({
    mutationFn: () =>
      usersApi.patch(id!, {
        full_name: fullName,
        role,
        employee_code: employeeCode,
        is_active: isActive,
        password: password || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setPassword("");
    },
  });

  const activate = useMutation({
    mutationFn: () => usersApi.activate(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  const deactivate = useMutation({
    mutationFn: () => usersApi.deactivate(id!),
    onSuccess: () => {
      setConfirmDeactivate(false);
      void qc.invalidateQueries({ queryKey: ["user", id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  if (!isNew && detail.isLoading) return <LoadingState />;
  if (!isNew && detail.error)
    return <ErrorState titleKey="states.errorTitle" message={translateError(detail.error)} />;

  const mutation = isNew ? create : patch;
  const errMsg = mutation.error ? translateError(mutation.error) : null;
  const isAdminRole = ADMIN_ROLES.includes(role);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isNew) create.mutate();
    else patch.mutate();
  };

  return (
    <div>
      <PageHeader
        title={isNew ? t("users.editor.titleNew") : t("users.editor.titleEdit")}
        subtitle={detail.data?.email}
        actions={
          <Link to="/users">
            <Button variant="secondary">{t("users.editor.back")}</Button>
          </Link>
        }
      />

      <div className="stack">
        <Card title={t("users.editor.detailsCardTitle")}>
          <form
            onSubmit={onSubmit}
            style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}
          >
            {errMsg && (
              <div style={{ gridColumn: "1 / -1" }}>
                <Alert tone="danger">{errMsg}</Alert>
              </div>
            )}
            {isAdminRole && (
              <div style={{ gridColumn: "1 / -1" }}>
                <Alert tone="warning" title={t("users.editor.adminWarningTitle")}>
                  {t("users.editor.adminWarningBody")}
                </Alert>
              </div>
            )}

            <FormField label={t("users.editor.field.email")} required>
              {(fid) => (
                <Input
                  id={fid}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={!isNew}
                />
              )}
            </FormField>

            <FormField label={t("users.editor.field.role")} required>
              {(fid) => (
                <Select
                  id={fid}
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`users.roles.${r}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>

            <FormField label={t("users.editor.field.fullName")}>
              {(fid) => (
                <Input
                  id={fid}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              )}
            </FormField>

            <FormField label={t("users.editor.field.employeeCode")}>
              {(fid) => (
                <Input
                  id={fid}
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                />
              )}
            </FormField>

            <FormField label={t("common.status")}>
              {(fid) => (
                <Select
                  id={fid}
                  value={isActive ? "true" : "false"}
                  onChange={(e) => setIsActive(e.target.value === "true")}
                >
                  <option value="true">{t("common.active")}</option>
                  <option value="false">{t("common.inactive")}</option>
                </Select>
              )}
            </FormField>

            {isAdminRole && (
              <FormField
                label={isNew ? t("users.editor.field.password") : t("users.editor.field.passwordOptional")}
                hint={isNew ? undefined : t("users.editor.passwordHint")}
                required={isNew}
              >
                {(fid) => (
                  <Input
                    id={fid}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required={isNew}
                  />
                )}
              </FormField>
            )}

            <div
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              {!isNew && detail.data && (
                <>
                  {detail.data.is_active ? (
                    <Button variant="danger" type="button" onClick={() => setConfirmDeactivate(true)}>
                      {t("users.actions.deactivate")}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      type="button"
                      loading={activate.isPending}
                      onClick={() => activate.mutate()}
                    >
                      {t("users.actions.activate")}
                    </Button>
                  )}
                </>
              )}
              <Button type="submit" loading={mutation.isPending}>
                {isNew ? t("users.editor.submitCreate") : t("common.save")}
              </Button>
            </div>
          </form>
        </Card>

        {!isNew && detail.data && (
          <Card title={t("users.detail.summaryCardTitle")}>
            <DescriptionList
              items={[
                { labelKey: "users.list.column.role", value: <Badge tone="info">{t(`users.roles.${detail.data.role}`)}</Badge> },
                { labelKey: "users.list.column.email", value: detail.data.email },
                { labelKey: "users.list.column.employeeCode", value: detail.data.employee_code || "—" },
                {
                  labelKey: "common.status",
                  value: detail.data.is_active ? (
                    <Badge tone="success">{t("common.active")}</Badge>
                  ) : (
                    <Badge tone="danger">{t("common.inactive")}</Badge>
                  ),
                },
                { labelKey: "users.list.column.lastLogin", value: formatDateTime(detail.data.last_login) },
                { labelKey: "common.createdAt", value: formatDateTime(detail.data.created_at) },
              ]}
            />
          </Card>
        )}

        {!isNew && (
          <Card title={t("users.detail.devicesCardTitle")} padding="none">
            <UserDevicesTable query={devices} />
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeactivate}
        title={t("users.confirmDeactivate.title")}
        body={t("users.confirmDeactivate.body")}
        loading={deactivate.isPending}
        onCancel={() => setConfirmDeactivate(false)}
        onConfirm={() => deactivate.mutate()}
      />
    </div>
  );
}

type UserDevicesQuery = ReturnType<typeof useQuery<DeviceListRow[]>>;

function UserDevicesTable({ query }: { query: UserDevicesQuery }) {
  if (query.isLoading) return <LoadingState />;
  if (query.error)
    return <ErrorState titleKey="states.errorTitle" message={translateError(query.error)} />;
  if (!query.data) return null;
  const columns: Column<DeviceListRow>[] = [
    {
      key: "uid",
      header: t("users.detail.deviceColumn.uid"),
      render: (row) => (
        <Link to={`/devices/${row.id}`}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{row.device_uid}</span>
        </Link>
      ),
    },
    {
      key: "platform",
      header: t("users.detail.deviceColumn.platform"),
      render: (row) => <Badge tone="info">{t(`common.platform.${row.platform}`, row.platform)}</Badge>,
    },
    {
      key: "model",
      header: t("users.detail.deviceColumn.model"),
      render: (row) => row.model || "—",
    },
    {
      key: "last_seen",
      header: t("users.detail.deviceColumn.lastSeen"),
      render: (row) => formatDateTime(row.last_seen_at),
    },
  ];
  return (
    <Table
      rows={query.data}
      rowKey={(row) => row.id}
      columns={columns}
      empty={<EmptyState titleKey="users.detail.devicesEmptyTitle" bodyKey="users.detail.devicesEmptyBody" />}
    />
  );
}

